import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react'
import { Row, Col } from 'react-bootstrap'
import moment from 'moment'
import { chain } from 'lodash'
import {
  AdminTable,
  IconButton,
  InputSearch,
  ClampedCard,
  Content,
  PreUploadModal,
  ProgramEditModal,
} from 'components'
import { FaEdit, FaRegTrashAlt, FaPlus, FaDownload } from 'react-icons/fa'
import {
  DATE_MOMENT_FORMAT,
  STAGED_SCHOLARSHIPS,
  STAGED_STATUS,
} from 'utils/constants'
import { I18nContext } from 'i18n'
import { useAlert } from 'react-alert'
import { getProgramValue } from 'utils/getProgramValue'
import TypeOfChange from './TypeOfChange'
import { ToggleHighlight, TableWrapper } from './style'
import SendForReviewButton from './SendForReviewButton'
import SendForReviewModal from './SendForReviewModal'
import { useGetAll } from 'hooks/scholarshipsStage/useGetAll'
import { useTransitionStatus } from 'hooks/scholarshipsStage/useTransitionStatus'
import { useDelete } from 'hooks/scholarshipsStage/useDelete'
import { getCurrentUser } from 'utils/getCurrentUser'

const columns = [
  { name: 'typeOfChange', className: 'w-8' },
  { name: 'programName', sortable: true, resizable: true, className: 'w-30' },
  {
    name: 'scholarshipMaximumAward',
    sortable: true,
    type: 'Currency',
    className: 'w-8',
  },
  { name: 'openDate', sortable: true, type: 'Date', className: 'w-8' },
  { name: 'closeDate', sortable: true, type: 'Date', className: 'w-8' },
  { name: 'status', sortable: true, className: 'w-8' },
  { name: 'canEdit', hidden: true },
  { name: 'canDelete', hidden: true },
  { name: 'canView', hidden: true },
  { name: 'id', key: true },
]

const permissions = {
  delete: 'canManageMemberScholarships',
}

const NO_CHANGE = 'No Change'

function fieldWasChanged(stagedScholarship, fieldName) {
  return chain(stagedScholarship)
    .get('differences.data')
    .find(
      ({ fieldKey, status }) => fieldKey === fieldName && status !== NO_CHANGE
    )
    .value()
}

function DisplayChangedDate({ stagedScholarship, json, dateField }) {
  const value = getProgramValue(json, dateField)
  const displayValue = value ? moment(value).format(DATE_MOMENT_FORMAT) : null
  return (
    displayValue && (
      <ToggleHighlight
        className="mx-auto"
        active={fieldWasChanged(stagedScholarship, dateField)}
      >
        {displayValue}
      </ToggleHighlight>
    )
  )
}

const ProgramUpdatesPage = ({ getStagedCount }) => {
  const { translate } = useContext(I18nContext)
  const [showUpload, setShowUpload] = useState(false)
  const [scholarshipForReview, setScholarshipForReview] = useState()
  const [data, setData] = useState()
  const alert = useAlert()

  // Add/Edit
  const [editProgramId, setEditProgramId] = useState()
  const [showEditModal, setShowEditModal] = useState(false)
  const { organization } = useMemo(getCurrentUser, [])
  // --

  const tooltipEdit = useMemo(() => translate('program_updates_tooltip_edit'), [
    translate,
  ])
  const tooltipSend = useMemo(() => translate('program_updates_tooltip_send'), [
    translate,
  ])

  const [params, setParams] = useState({
    status: [
      STAGED_STATUS.MEMBER_DRAFTING,
      STAGED_STATUS.SUBMITTED_FOR_REVIEW,
      STAGED_STATUS.PENDING_REVIEW,
    ],
  })
  const {
    data: dataPrograms,
    loading: loadingPrograms,
    reFetch: reFetchScholarships,
  } = useGetAll(params)

  const {
    data: dataTransition,
    loading: loadingTransition,
    reFetch: transitionStatus,
  } = useTransitionStatus({
    from: STAGED_STATUS.MEMBER_DRAFTING,
    to: STAGED_STATUS.SUBMITTED_FOR_REVIEW,
    scope: scholarshipForReview?.id || scholarshipForReview,
  })

  const { data: dataDelete, loading: loadingDelete, deleteRecord } = useDelete()

  useEffect(() => {
    if (dataTransition || dataDelete) {
      reFetchScholarships()
      hideTransitionModal()
    }
  }, [dataTransition, dataDelete])

  useEffect(() => {
    if (dataPrograms?.data) {
      //Map the data for the admin table
      const newData = dataPrograms.data.map((stagedScholarship) => {
        const { id, json, status } = stagedScholarship
        return {
          id,
          typeOfChange: <TypeOfChange stagedScholarship={stagedScholarship} />,
          programName: (
            <ClampedCard
              title={getProgramValue(json, 'programName')}
              description={getProgramValue(json, 'programSelfDescription')}
            />
          ),
          scholarshipMaximumAward: getProgramValue(
            json,
            'scholarshipMaximumAward'
          ),
          openDate: (
            <DisplayChangedDate
              dateField="openDate"
              {...{ stagedScholarship, json }}
            />
          ),
          closeDate: (
            <DisplayChangedDate
              dateField="closeDate"
              {...{ stagedScholarship, json }}
            />
          ),
          status: status?.message,
          statusName: status.name,
          isNew: !stagedScholarship.differences,
        }
      })
      setData(newData)
      getStagedCount()
    }
  }, [dataPrograms])

  useEffect(() => {
    reFetchScholarships()
  }, [])

  const handleSort = useCallback(
    (orderColumn, orderDirection) =>
      setParams({
        ...params,
        orderColumn,
        orderDirection,
      }),
    [params, setParams]
  )

  const handlePageChange = useCallback(
    (page, pageSize) =>
      setParams({
        ...params,
        page,
        pageSize,
      }),
    [params, setParams]
  )

  const handleProgramNameChange = useCallback(
    (programName) =>
      setParams({
        ...params,
        page: 1,
        programName,
      }),
    [params, setParams]
  )

  const handleEdit = useCallback(
    (scholarship) => {
      setEditProgramId(scholarship.id)
      setShowEditModal(true)
    },
    [setEditProgramId, setShowEditModal]
  )

  const handleHideEditModal = useCallback(
    (confirmed) => {
      setShowEditModal(false)
      if (confirmed) {
        setEditProgramId()
        reFetchScholarships()
      }
    },
    [setShowEditModal, setEditProgramId, reFetchScholarships]
  )

  const handleClickNew = useCallback(() => {
    setEditProgramId()
    setShowEditModal(true)
  }, [setEditProgramId, setShowEditModal])

  const hideTransitionModal = useCallback(setScholarshipForReview.bind(null), [
    setScholarshipForReview,
  ])

  return (
    <Content>
      <Row>
        <Col lg={3} md={4}>
          <InputSearch
            onChange={handleProgramNameChange}
            value={params?.programName}
            placeholder="Filter this List"
            className="ml-3 mb-3 tiny"
          />
        </Col>
        <Col className="text-right mr-2">
          <IconButton
            text={translate('program_list_new')}
            icon={<FaPlus />}
            onClick={handleClickNew}
            className="small"
            tooltip={translate('program_updates_tooltip_new')}
          />
          <IconButton
            icon={<FaPlus />}
            text={translate('data_center_upload_title')}
            onClick={() => setShowUpload(true)}
            className="small"
            tooltip={translate('program_updates_tooltip_upload')}
          />

          <IconButton
            icon={<FaDownload />}
            text={translate('program_list_export')}
            onClick={() => alert.info('Feature to Come, Not Yet Implemented')}
            className="small"
            tooltip={translate('program_updates_tooltip_export')}
          />

          <SendForReviewButton
            variant="primary"
            onClick={() => setScholarshipForReview('*')}
            tooltip={translate('program_updates_tooltip_send_all')}
            className="py-2"
            disabled={dataPrograms?.meta.draftCount === 0}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <TableWrapper>
            <AdminTable
              defaultSort="programName"
              scope="program-updates"
              server
              onSort={handleSort}
              onPageChange={handlePageChange}
              meta={dataPrograms?.meta}
              permissions={permissions}
              loading={loadingPrograms || loadingTransition || loadingDelete}
              onDelete={deleteRecord}
              onDeleteConfirmationText="Are you sure you want to discard your changes? This action cannot be undone."
              customComponents={{
                delete: (scholarship) => (
                  <IconButton
                    icon={<FaRegTrashAlt />}
                    className="mr-0 pr-0 small"
                    red
                    text={translate(
                      scholarship.isNew
                        ? 'discard_new_program'
                        : 'discard_changes'
                    )}
                    tooltip={translate(
                      scholarship.isNew
                        ? 'program_updates_tooltip_discard_new_program'
                        : 'program_updates_tooltip_discard_changes'
                    )}
                    disabled={
                      scholarship.statusName === STAGED_STATUS.PENDING_REVIEW
                    }
                  />
                ),
              }}
              customActions={[
                (scholarship) => (
                  <>
                    <IconButton
                      onClick={handleEdit.bind(null, scholarship)}
                      icon={<FaEdit />}
                      className="mr-0 pr-0 small"
                      text="Edit"
                      style={{ transform: 'translate(1px)' }}
                      disabled={
                        scholarship.statusName === STAGED_STATUS.PENDING_REVIEW
                      }
                      tooltip={tooltipEdit}
                    />
                    <br />
                  </>
                ),
                (scholarship) => (
                  <>
                    <SendForReviewButton
                      onClick={() => setScholarshipForReview(scholarship)}
                      label={translate('send_for_review')}
                      disabled={
                        scholarship.statusName !== STAGED_STATUS.MEMBER_DRAFTING
                      }
                      tooltip={tooltipSend}
                    />
                    <br />
                  </>
                ),
              ]}
              {...{ columns, data }}
            />
          </TableWrapper>
        </Col>
      </Row>
      <PreUploadModal
        show={showUpload}
        setShow={setShowUpload}
        onHide={() => setShowUpload(false)}
      />
      <SendForReviewModal
        show={!!scholarshipForReview}
        onHide={hideTransitionModal}
        onConfirm={transitionStatus}
        draftCount={
          typeof scholarshipForReview === 'string' &&
          dataPrograms?.meta.draftCount
        }
      />
      {showEditModal && (
        <ProgramEditModal
          show
          onHide={handleHideEditModal}
          toolbar
          items={data}
          id={editProgramId}
          organization={organization}
          source={STAGED_SCHOLARSHIPS}
        />
      )}
    </Content>
  )
}

export default ProgramUpdatesPage
